import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { motion } from 'framer-motion';
import {
  EyeIcon,
  EyeSlashIcon,
  ShieldCheckIcon,
  KeyIcon,
  EnvelopeIcon,
  ExclamationTriangleIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { useAuth, useAuthActions } from '@/stores/auth';
import { useBrand } from '@/components/providers/BrandProvider';
import { LoginCredentials } from '@/types/auth';
import toast from 'react-hot-toast';

const loginSchema = z.object({
  email: z
    .string()
    .min(1, 'Email is required')
    .email('Please enter a valid email address'),
  password: z
    .string()
    .min(1, 'Password is required')
    .min(8, 'Password must be at least 8 characters'),
  mfaCode: z
    .string()
    .optional()
    .refine((val) => !val || val.length === 6, 'MFA code must be 6 digits'),
  rememberMe: z.boolean().default(false),
});

type LoginFormData = z.infer<typeof loginSchema>;

export default function Login() {
  const navigate = useNavigate();
  const { isAuthenticated, error: authError } = useAuth();
  const { login, clearError } = useAuthActions();
  const brand = useBrand();

  const [showPassword, setShowPassword] = useState(false);
  const [showMFA, setShowMFA] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    clearErrors,
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema as unknown as Parameters<typeof zodResolver>[0]),
    defaultValues: {
      email: '',
      password: '',
      mfaCode: '',
      rememberMe: false,
    },
  });

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  // Clear auth errors only when component mounts
  useEffect(() => {
    clearError();
  }, [clearError]);

  /**
   * Parse error messages and return user-friendly versions
   */
  const getUserFriendlyErrorMessage = (error: unknown): string => {
    const errorMsg = ((error as Error)?.message || (error as { toString?: () => string })?.toString?.()) ?? '';

    // Check for specific error types
    if (errorMsg.toLowerCase().includes('network') || errorMsg.toLowerCase().includes('fetch')) {
      return 'Unable to connect to the server. Please check your internet connection and try again.';
    }

    if (errorMsg.toLowerCase().includes('timeout')) {
      return 'The request timed out. Please check your connection and try again.';
    }

    if (errorMsg.toLowerCase().includes('invalid credentials') ||
      errorMsg.toLowerCase().includes('incorrect password') ||
      errorMsg.toLowerCase().includes('invalid email or password')) {
      return 'The email or password you entered is incorrect. Please try again.';
    }

    if (errorMsg.toLowerCase().includes('user not found') ||
      errorMsg.toLowerCase().includes('account not found')) {
      return 'No account found with this email address. Please check your email or contact support.';
    }

    if (errorMsg.toLowerCase().includes('account locked') ||
      errorMsg.toLowerCase().includes('account disabled')) {
      return 'Your account has been locked. Please contact support for assistance.';
    }

    if (errorMsg.toLowerCase().includes('too many attempts') ||
      errorMsg.toLowerCase().includes('rate limit')) {
      return 'Too many login attempts. Please wait a few minutes before trying again.';
    }

    if (errorMsg.toLowerCase().includes('mfa') ||
      errorMsg.toLowerCase().includes('two-factor') ||
      errorMsg.toLowerCase().includes('2fa')) {
      return 'Two-factor authentication is required. Please enter your 6-digit code.';
    }

    if (errorMsg.toLowerCase().includes('invalid mfa') ||
      errorMsg.toLowerCase().includes('incorrect code')) {
      return 'The authentication code you entered is incorrect. Please try again.';
    }

    if (errorMsg.toLowerCase().includes('expired')) {
      return 'Your session has expired. Please log in again.';
    }

    if (errorMsg.toLowerCase().includes('server error') ||
      errorMsg.toLowerCase().includes('500')) {
      return 'We\'re experiencing technical difficulties. Please try again later or contact support.';
    }

    if (errorMsg.toLowerCase().includes('unauthorized') ||
      errorMsg.toLowerCase().includes('401')) {
      return 'Authentication failed. Please check your credentials and try again.';
    }

    // Return original message if more specific, otherwise generic message
    return errorMsg || 'Unable to sign in. Please check your credentials and try again.';
  };

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    clearErrors();
    clearError(); // Clear any previous auth errors

    try {
      const credentials: LoginCredentials = {
        email: data.email,
        password: data.password,
        mfaCode: data.mfaCode ?? undefined,
        rememberMe: data.rememberMe,
      };

      await login(credentials);

      toast.success('Welcome back! Redirecting to your home dashboard...', { duration: 3000 });
      navigate('/', { replace: true });
    } catch (error: unknown) {
      const err = error as Error;
      console.error('Login error:', err);

      const friendlyMessage = getUserFriendlyErrorMessage(err);

      // Check if MFA is required
      if (err.message?.toLowerCase().includes('mfa') ||
        err.message?.toLowerCase().includes('two-factor') ||
        err.message?.toLowerCase().includes('2fa')) {
        setShowMFA(true);
        toast.error(friendlyMessage, {
          duration: 8000,
          icon: '🔐',
        });
      } else {
        toast.error(friendlyMessage, {
          duration: 8000,
          icon: '⚠️',
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: 'easeOut',
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.4, ease: 'easeOut' },
    },
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-secondary-50 dark:from-secondary-900 dark:via-secondary-800 dark:to-secondary-900 flex items-center justify-center p-4">
      {/* Background pattern */}
      <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2260%22%20height%3D%2260%22%20viewBox%3D%220%200%2060%2060%22%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%3E%3Cg%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Cg%20fill%3D%22%23e2e8f0%22%20fill-opacity%3D%220.3%22%3E%3Ccircle%20cx%3D%2230%22%20cy%3D%2230%22%20r%3D%221%22/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')] dark:bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2260%22%20height%3D%2260%22%20viewBox%3D%220%200%2060%2060%22%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%3E%3Cg%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Cg%20fill%3D%22%23374151%22%20fill-opacity%3D%220.1%22%3E%3Ccircle%20cx%3D%2230%22%20cy%3D%2230%22%20r%3D%221%22/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')]" />

      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="w-full max-w-md relative"
      >
        {/* Logo and Header */}
        <motion.div variants={itemVariants} className="text-center mb-8">
          <div className="mx-auto w-80 h-24 flex items-center justify-center mb-3">
            <img
              src={`/${brand.assets.logo}`}
              alt={`${brand.name} Logo`}
              className="w-full h-full object-contain drop-shadow-2xl"
            />
          </div>
          <h1 className="text-3xl font-bold text-secondary-900 dark:text-white mb-2">
            {brand.appTitle}
          </h1>
          <p className="text-secondary-600 dark:text-secondary-400">
            Sign in to access your command dashboard
          </p>
        </motion.div>

        {/* Login Form */}
        <motion.div variants={itemVariants}>
          <Card variant="glass" className="backdrop-blur-xl border-white/20 dark:border-secondary-700/20">
            <CardHeader className="pb-4">
              <CardTitle className="text-xl text-center">Welcome Back</CardTitle>
              <CardDescription className="text-center">
                Enter your credentials to continue
              </CardDescription>
            </CardHeader>

            <CardContent>
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                {/* Global Error Message */}
                {authError && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="bg-danger-50 border border-danger-200 rounded-lg p-4 flex items-start space-x-3 dark:bg-danger-900/20 dark:border-danger-800"
                  >
                    <ExclamationTriangleIcon className="w-5 h-5 text-danger-600 dark:text-danger-400 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-danger-700 dark:text-danger-300 flex-1">
                      {getUserFriendlyErrorMessage({ message: authError })}
                    </p>
                    <button
                      type="button"
                      onClick={clearError}
                      className="text-danger-500 hover:text-danger-700 dark:text-danger-400 dark:hover:text-danger-200 transition-colors focus:outline-none focus:ring-2 focus:ring-danger-500 rounded"
                      aria-label="Dismiss error"
                    >
                      <XMarkIcon className="w-5 h-5" />
                    </button>
                  </motion.div>
                )}

                {/* Email Field */}
                <div>
                  <Input
                    {...register('email')}
                    type="email"
                    label="Email Address"
                    placeholder="Enter your email"
                    leftIcon={<EnvelopeIcon className="w-4 h-4" />}
                    error={errors.email?.message}
                    disabled={isLoading}
                    autoComplete="email"
                    required
                  />
                </div>

                {/* Password Field */}
                <div>
                  <Input
                    {...register('password')}
                    type={showPassword ? 'text' : 'password'}
                    label="Password"
                    placeholder="Enter your password"
                    leftIcon={<KeyIcon className="w-4 h-4" />}
                    rightIcon={
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="focus:outline-none hover:text-secondary-600 dark:hover:text-secondary-300"
                        tabIndex={-1}
                      >
                        {showPassword ? (
                          <EyeSlashIcon className="w-4 h-4" />
                        ) : (
                          <EyeIcon className="w-4 h-4" />
                        )}
                      </button>
                    }
                    error={errors.password?.message}
                    disabled={isLoading}
                    autoComplete="current-password"
                    required
                  />
                </div>

                {/* MFA Field (conditional) */}
                {showMFA && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                  >
                    <Input
                      {...register('mfaCode')}
                      type="text"
                      label="Two-Factor Authentication"
                      placeholder="Enter 6-digit code"
                      error={errors.mfaCode?.message}
                      disabled={isLoading}
                      autoComplete="one-time-code"
                      maxLength={6}
                      className="text-center tracking-widest font-mono"
                    />
                  </motion.div>
                )}

                {/* Remember Me */}
                <div className="flex items-center justify-between">
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      {...register('rememberMe')}
                      type="checkbox"
                      className="rounded border-secondary-300 text-primary-600 focus:ring-primary-500 dark:border-secondary-600 dark:bg-secondary-800"
                      disabled={isLoading}
                    />
                    <span className="text-sm text-secondary-700 dark:text-secondary-300">
                      Remember me
                    </span>
                  </label>

                  <Link
                    to="/forgot-password"
                    className="text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 font-medium"
                  >
                    Forgot password?
                  </Link>
                </div>

                {/* Submit Button */}
                <Button
                  type="submit"
                  className="w-full"
                  size="lg"
                  loading={isLoading}
                  disabled={isLoading}
                >
                  {isLoading ? 'Signing in...' : 'Sign In'}
                </Button>
              </form>

              {/* Additional Links */}
              <div className="mt-6 pt-6 border-t border-secondary-200 dark:border-secondary-700">
                <p className="text-center text-sm text-secondary-600 dark:text-secondary-400">
                  Need help?{' '}
                  <Link
                    to="/contact"
                    className="text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 font-medium"
                  >
                    Contact Support
                  </Link>
                </p>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Security Notice */}
        <motion.div
          variants={itemVariants}
          className="mt-6 text-center text-xs text-secondary-500 dark:text-secondary-400"
        >
          <div className="flex items-center justify-center space-x-1 mb-2">
            <ShieldCheckIcon className="w-4 h-4" />
            <span>Secured with enterprise-grade encryption</span>
          </div>
          <p>
            By signing in, you agree to our{' '}
            <Link to="/terms" className="underline hover:text-secondary-700 dark:hover:text-secondary-300">
              Terms of Service
            </Link>{' '}
            and{' '}
            <Link to="/privacy" className="underline hover:text-secondary-700 dark:hover:text-secondary-300">
              Privacy Policy
            </Link>
          </p>
        </motion.div>
      </motion.div>
    </div>
  );
}